import { FormEvent, useEffect } from "react";
import { Head, Link, useForm } from "@inertiajs/react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import MapPicker from "@/components/desk/MapPicker";
import PropertyMediaSidebar from "@/components/desk/PropertyMediaSidebar";
import { ManagedImage } from "@/components/desk/SortableImageUploader";
import {
  PROPERTY_TABS,
  STATUS_OPTIONS,
  CONTRACT_TYPE_OPTIONS,
  RENT_FREQUENCY_OPTIONS,
  AMENITIES_OPTIONS,
  getYearValue,
  toConstructionDate,
} from "./constants";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const KIND_OPTIONS = [
  "منزل مستقل",
  "شقة",
  "فيلا",
  "مزرعة",
  "متجر",
  "مكتب",
  "عيادة",
  "صالة صناعية",
  "مستودع",
  "ورشة",
  "أرض سكنية",
  "أرض تجارية",
  "أرض صناعية",
  "أرض زراعية",
];

const LAND_KINDS = ["أرض سكنية", "أرض تجارية", "أرض صناعية", "أرض زراعية"];

const LICENSE_OPTIONS = ["صناعي", "تجاري", "زراعي", "بناء", "لا يوجد رخصة"];

const DELIVERY_TYPE_OPTIONS = ["جاهز", "قيد الإنشاء"];
const FLOOR_OPTIONS = [
  "تحت الأول",
  "الطابق الأرضي",
  "الأول",
  "الثاني",
  "الثالث",
  "الرابع",
  "الخامس",
  "السادس",
];
const BATHROOM_OPTIONS = ["1", "2", "3", "4", "5"];

interface Agency {
  id: number;
  name: string;
}

interface Region {
  id: number;
  name: string;
}

type PropertyFormData = {
  agency_id: string;
  region_id: string;
  title: string;
  kind: string;
  contract_type: string;
  license: string;
  delivery_type: string;
  description: string;
  price: string;
  total_area: string;
  liveable_area: string;
  rooms_count: string;
  bathroom_count: string;
  floor: string;
  construction_date: string;
  latitude: string;
  longitude: string;
  is_featured: boolean;
  status: string;
  rent_frequency: string;
  amenities: string[];
};

const toFormString = (value?: string | number | null) =>
  value === undefined || value === null ? "" : `${value}`;

interface Props {
  property: Partial<
    PropertyFormData & {
      agency_id?: number;
      region_id?: number;
      id?: number;
      images?: ManagedImage[];
    }
  >;
  agencies: Agency[];
  regions: Region[];
}

export default function Create({ property, agencies, regions }: Props) {
  const { data, setData, post, processing, errors } = useForm<PropertyFormData>({
    agency_id: toFormString(property?.agency_id),
    region_id: toFormString(property?.region_id),
    title: property?.title || "",
    kind: property?.kind || "",
    contract_type: property?.contract_type || "",
    license: property?.license || "",
    delivery_type: property?.delivery_type || "",
    description: property?.description || "",
    price: toFormString(property?.price),
    total_area: toFormString(property?.total_area),
    liveable_area: toFormString(property?.liveable_area),
    rooms_count: toFormString(property?.rooms_count),
    bathroom_count: toFormString(property?.bathroom_count),
    floor: property?.floor || "",
    construction_date: property?.construction_date || "",
    latitude: property?.latitude || "",
    longitude: property?.longitude || "",
    is_featured: property?.is_featured || false,
    status: property?.status || "draft",
    rent_frequency: property?.rent_frequency || "",
    amenities: property?.amenities || [],
  });

  useEffect(() => {
    if (!LAND_KINDS.includes(data.kind) && data.license) {
      setData("license", "");
    }
  }, [data.kind, data.license, setData]);

  useEffect(() => {
    if (property && Object.keys(errors).length > 0) {
      setData({
        agency_id: toFormString(property?.agency_id),
        region_id: toFormString(property?.region_id),
        title: property?.title || "",
        kind: property?.kind || "",
        contract_type: property?.contract_type || "",
        license: property?.license || "",
        delivery_type: property?.delivery_type || "",
        description: property?.description || "",
        price: toFormString(property?.price),
        total_area: toFormString(property?.total_area),
        liveable_area: toFormString(property?.liveable_area),
        rooms_count: toFormString(property?.rooms_count),
        bathroom_count: toFormString(property?.bathroom_count),
        floor: property?.floor || "",
        construction_date: property?.construction_date || "",
        latitude: property?.latitude || "",
        longitude: property?.longitude || "",
        is_featured: property?.is_featured || false,
        status: property?.status || "draft",
        rent_frequency: property?.rent_frequency || "",
        amenities: property?.amenities || [],
      });
    }
  }, [property, errors, setData]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    post("/desk/properties");
  };

  const renderFieldError = (field: keyof PropertyFormData) => {
    const error = errors[field];
    if (!error) return null;
    return <p className="text-sm text-destructive">{error}</p>;
  };

  const setAmenity = (amenity: string, enabled: boolean) => {
    const nextAmenities = enabled
      ? Array.from(new Set([...data.amenities, amenity]))
      : data.amenities.filter((value) => value !== amenity);
    setData("amenities", nextAmenities);
  };

  const propertyImages = property?.images || [];

  return (
    <>
      <Head title="إضافة عقار جديد" />

      <Card>
        <CardHeader>
          <CardTitle>إضافة عقار جديد</CardTitle>
          <CardDescription>أدخل تفاصيل العقار لإضافته إلى النظام</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex flex-wrap items-center justify-end gap-3">
              <Button variant="outline" asChild>
                <Link href="/desk/properties">إلغاء</Link>
              </Button>
              <Button type="submit" disabled={processing}>
                {processing ? "جاري الحفظ..." : "حفظ العقار"}
              </Button>
            </div>
            <div className="md:grid md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-6">
              <div className="space-y-4">
                <div className="rounded-2xl border border-border/60 bg-card-light dark:bg-card-dark p-5 shadow-sm">
                  <Tabs defaultValue={PROPERTY_TABS[0].id} className="space-y-6">
                    <TabsList className="flex flex-wrap gap-2">
                      {PROPERTY_TABS.map((tab) => (
                        <TabsTrigger key={tab.id} value={tab.id}>
                          {tab.label}
                        </TabsTrigger>
                      ))}
                    </TabsList>
                    <TabsContent value="basic">
                      <div className="space-y-6">
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label htmlFor="agency_id">الوكالة</Label>
                            <Select
                              value={data.agency_id}
                              onValueChange={(value) => setData("agency_id", value)}
                            >
                              <SelectTrigger
                                id="agency_id"
                                className="w-full"
                                aria-invalid={errors.agency_id ? "true" : "false"}
                              >
                                <SelectValue placeholder="اختر الوكالة" />
                              </SelectTrigger>
                              <SelectContent>
                                {agencies.map((agency) => (
                                  <SelectItem key={agency.id} value={agency.id.toString()}>
                                    {agency.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {renderFieldError("agency_id")}
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="region_id">المنطقة</Label>
                            <Select
                              value={data.region_id}
                              onValueChange={(value) => setData("region_id", value)}
                            >
                              <SelectTrigger
                                id="region_id"
                                className="w-full"
                                aria-invalid={errors.region_id ? "true" : "false"}
                              >
                                <SelectValue placeholder="اختر المنطقة" />
                              </SelectTrigger>
                              <SelectContent>
                                {regions.map((region) => (
                                  <SelectItem key={region.id} value={region.id.toString()}>
                                    {region.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {renderFieldError("region_id")}
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="title">عنوان الإعلان</Label>
                            <Input
                              id="title"
                              value={data.title}
                              onChange={(event) => setData("title", event.target.value)}
                              placeholder="عنوان موجز وجذاب للعقار"
                              aria-invalid={errors.title ? "true" : "false"}
                            />
                            {renderFieldError("title")}
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="kind">النوع</Label>
                            <Select
                              value={data.kind}
                              onValueChange={(value) => setData("kind", value)}
                            >
                              <SelectTrigger
                                id="kind"
                                className="w-full"
                                aria-invalid={errors.kind ? "true" : "false"}
                              >
                                <SelectValue placeholder="اختر النوع" />
                              </SelectTrigger>
                              <SelectContent>
                                {KIND_OPTIONS.map((option) => (
                                  <SelectItem key={option} value={option}>
                                    {option}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {renderFieldError("kind")}
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="status">الحالة</Label>
                            <Select
                              value={data.status}
                              onValueChange={(value) => setData("status", value)}
                            >
                              <SelectTrigger
                                id="status"
                                className="w-full"
                                aria-invalid={errors.status ? "true" : "false"}
                              >
                                <SelectValue placeholder="اختر الحالة" />
                              </SelectTrigger>
                              <SelectContent>
                                {STATUS_OPTIONS.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {renderFieldError("status")}
                          </div>

                          {LAND_KINDS.includes(data.kind) && (
                            <div className="space-y-2">
                              <Label htmlFor="license">الرخصة</Label>
                              <Select
                                value={data.license}
                                onValueChange={(value) => setData("license", value)}
                              >
                                <SelectTrigger
                                  id="license"
                                  className="w-full"
                                  aria-invalid={errors.license ? "true" : "false"}
                                >
                                  <SelectValue placeholder="اختر الرخصة" />
                                </SelectTrigger>
                                <SelectContent>
                                  {LICENSE_OPTIONS.map((option) => (
                                    <SelectItem key={option} value={option}>
                                      {option}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {renderFieldError("license")}
                            </div>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="description">الوصف</Label>
                          <Textarea
                            id="description"
                            value={data.description}
                            onChange={(event) => setData("description", event.target.value)}
                            placeholder="أدخل وصفاً مفصلاً عن العقار"
                            aria-invalid={errors.description ? "true" : "false"}
                          />
                          {renderFieldError("description")}
                        </div>

                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="is_featured"
                            checked={data.is_featured}
                            onCheckedChange={(checked) => setData("is_featured", checked === true)}
                          />
                          <Label htmlFor="is_featured" className="cursor-pointer">
                            عقار مميز
                          </Label>
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="contract">
                      <div className="space-y-6">
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label htmlFor="contract_type">نوع العقد</Label>
                            <Select
                              value={data.contract_type}
                              onValueChange={(value) => setData("contract_type", value)}
                            >
                              <SelectTrigger
                                id="contract_type"
                                className="w-full"
                                aria-invalid={errors.contract_type ? "true" : "false"}
                              >
                                <SelectValue placeholder="اختر نوع العقد" />
                              </SelectTrigger>
                              <SelectContent>
                                {CONTRACT_TYPE_OPTIONS.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {renderFieldError("contract_type")}
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="delivery_type">نوع التسليم</Label>
                            <Select
                              value={data.delivery_type}
                              onValueChange={(value) => setData("delivery_type", value)}
                            >
                              <SelectTrigger
                                id="delivery_type"
                                className="w-full"
                                aria-invalid={errors.delivery_type ? "true" : "false"}
                              >
                                <SelectValue placeholder="اختر نوع التسليم" />
                              </SelectTrigger>
                              <SelectContent>
                                {DELIVERY_TYPE_OPTIONS.map((option) => (
                                  <SelectItem key={option} value={option}>
                                    {option}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {renderFieldError("delivery_type")}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="price">السعر (دولار أمريكي)</Label>
                          <Input
                            id="price"
                            type="number"
                            value={data.price}
                            onChange={(event) => setData("price", event.target.value)}
                            placeholder="1000000"
                            min="0"
                            aria-invalid={errors.price ? "true" : "false"}
                          />
                          {renderFieldError("price")}
                        </div>

                        {data.contract_type === "إيجار" && (
                          <div className="space-y-2">
                            <Label htmlFor="rent_frequency">تكرار الإيجار</Label>
                            <Select
                              value={data.rent_frequency}
                              onValueChange={(value) => setData("rent_frequency", value)}
                            >
                              <SelectTrigger
                                id="rent_frequency"
                                className="w-full"
                                aria-invalid={errors.rent_frequency ? "true" : "false"}
                              >
                                <SelectValue placeholder="اختر تكرار الإيجار" />
                              </SelectTrigger>
                              <SelectContent>
                                {RENT_FREQUENCY_OPTIONS.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {renderFieldError("rent_frequency")}
                          </div>
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="physical">
                      <div className="space-y-6">
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label htmlFor="total_area">المساحة الإجمالية (م²)</Label>
                            <Input
                              id="total_area"
                              type="number"
                              value={data.total_area}
                              onChange={(event) => setData("total_area", event.target.value)}
                              placeholder="150"
                              min="0"
                              aria-invalid={errors.total_area ? "true" : "false"}
                            />
                            {renderFieldError("total_area")}
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="liveable_area">المساحة القابلة للسكن (م²)</Label>
                            <Input
                              id="liveable_area"
                              type="number"
                              value={data.liveable_area}
                              onChange={(event) => setData("liveable_area", event.target.value)}
                              placeholder="120"
                              min="0"
                              aria-invalid={errors.liveable_area ? "true" : "false"}
                            />
                            {renderFieldError("liveable_area")}
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="rooms_count">عدد الغرف</Label>
                            <Input
                              id="rooms_count"
                              type="number"
                              value={data.rooms_count}
                              onChange={(event) => setData("rooms_count", event.target.value)}
                              placeholder="3"
                              min="0"
                              aria-invalid={errors.rooms_count ? "true" : "false"}
                            />
                            {renderFieldError("rooms_count")}
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="bathroom_count">عدد الحمامات</Label>
                            <Select
                              value={data.bathroom_count}
                              onValueChange={(value) => setData("bathroom_count", value)}
                            >
                              <SelectTrigger
                                id="bathroom_count"
                                className="w-full"
                                aria-invalid={errors.bathroom_count ? "true" : "false"}
                              >
                                <SelectValue placeholder="اختر عدد الحمامات" />
                              </SelectTrigger>
                              <SelectContent>
                                {BATHROOM_OPTIONS.map((option) => (
                                  <SelectItem key={option} value={option}>
                                    {option}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {renderFieldError("bathroom_count")}
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="floor">الطابق</Label>
                            <Select
                              value={data.floor}
                              onValueChange={(value) => setData("floor", value)}
                            >
                              <SelectTrigger
                                id="floor"
                                className="w-full"
                                aria-invalid={errors.floor ? "true" : "false"}
                              >
                                <SelectValue placeholder="اختر الطابق" />
                              </SelectTrigger>
                              <SelectContent>
                                {FLOOR_OPTIONS.map((option) => (
                                  <SelectItem key={option} value={option}>
                                    {option}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {renderFieldError("floor")}
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="construction_date">سنة البناء</Label>
                            <Input
                              id="construction_date"
                              type="number"
                              min="1800"
                              max={new Date().getFullYear().toString()}
                              placeholder="2020"
                              value={getYearValue(data.construction_date)}
                              onChange={(event) =>
                                setData("construction_date", toConstructionDate(event.target.value))
                              }
                              aria-invalid={errors.construction_date ? "true" : "false"}
                            />
                            {renderFieldError("construction_date")}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>اختيار الموقع على الخريطة</Label>
                          <MapPicker
                            latitude={data.latitude}
                            longitude={data.longitude}
                            onChange={(latitude, longitude) => {
                              setData("latitude", latitude);
                              setData("longitude", longitude);
                            }}
                          />
                        </div>

                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label htmlFor="latitude">خط العرض</Label>
                            <Input
                              id="latitude"
                              value={data.latitude}
                              readOnly
                              placeholder="33.5138"
                              aria-invalid={errors.latitude ? "true" : "false"}
                            />
                            {renderFieldError("latitude")}
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="longitude">خط الطول</Label>
                            <Input
                              id="longitude"
                              value={data.longitude}
                              readOnly
                              placeholder="36.2765"
                              aria-invalid={errors.longitude ? "true" : "false"}
                            />
                            {renderFieldError("longitude")}
                          </div>
                        </div>

                        <div className="space-y-3">
                          <Label>وسائل الراحة المتوفرة</Label>
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {AMENITIES_OPTIONS.map((option) => (
                              <div key={option.value} className="flex items-center gap-2">
                                <Checkbox
                                  id={`amenity-${option.value}`}
                                  checked={data.amenities.includes(option.value)}
                                  onCheckedChange={(checked) =>
                                    setAmenity(option.value, checked === true)
                                  }
                                />
                                <Label
                                  htmlFor={`amenity-${option.value}`}
                                  className="m-0 text-sm font-medium"
                                >
                                  {option.label}
                                </Label>
                              </div>
                            ))}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            اختر وسائل الراحة التي تتوفر بالعقار.
                          </p>
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              </div>

              <PropertyMediaSidebar propertyId={property?.id} images={propertyImages} />
            </div>
          </form>
        </CardContent>
      </Card>
    </>
  );
}
